import type { Expense, Category, AppSettings } from "@/models/types";

// Capa de servicios preparada para conectarse a una API REST.
// Actualmente funciona contra el estado local (store), pero abstrae
// las operaciones para poder sustituir la implementación por fetch() después.

const API_BASE_URL = "https://apicontrol.airmex.mx/api";

// ---------- Categorías ----------
const handleResponse = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    const errorBody = await res.text();
    throw new Error(`Error ${res.status}: ${errorBody}`);
  }
  return res.json();
};

export interface CategoryPayload {
  name: string;
  color?: string;
  icon?: string;
}

// Función para normalizar categorías desde el backend
const normalizeCategory = (cat: any): Category => ({
  id: cat.id || cat._id, // acepta ambos campos
  name: cat.name,
  color: cat.color || "",
  icon: cat.icon || "",
});

export const categoryApi = {
   list: async (): Promise<Category[]> => {
    const res = await fetch(`${API_BASE_URL}/categories/`);
    if (!res.ok) throw new Error("Error al obtener categorías");
    return res.json();
  },

  create: async (data: CategoryPayload): Promise<Category> => {
    const res = await fetch(`${API_BASE_URL}/categories/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error("Error al crear categoría");
    const created = await res.json();
    return normalizeCategory(created);
  },

  update: async (id: string, data: Partial<CategoryPayload>): Promise<Category> => {
    const res = await fetch(`${API_BASE_URL}/categories/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error("Error al actualizar categoría");
    const updated = await res.json();
    return normalizeCategory(updated);
  },

remove: async (id: string): Promise<void> => {
  const res = await fetch(`${API_BASE_URL}/categories/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const errorBody = await res.text();
    throw new Error(`Error al eliminar categoría: ${res.status} ${errorBody}`);
  }
},
};

// ---------- Stats API ----------
export async function getCategoryTotals(): Promise<{ category: Category; total: number }[]> {
  const res = await fetch(`${API_BASE_URL}/stats/category-totals`);
  if (!res.ok) throw new Error("Error al obtener totales");
  return res.json();
}
export interface ExpensePayload {
  date: string;
  description: string;
  category_id: string; // el backend espera category_id
  amount: number;
  payment_method: string; // el backend espera payment_method
  notes?: string;
  receipt?: any;
}

export const expenseApi = {
  // Obtener todos los gastos
  list: async (): Promise<Expense[]> => {
    const res = await fetch(`${API_BASE_URL}/expenses/`);
    const data = await handleResponse<any[]>(res);
    // Mapear snake_case a camelCase para el frontend
    return data.map((e: any) => ({
      ...e,
      categoryId: e.category_id ?? e.categoryId,
      paymentMethod: e.payment_method ?? e.paymentMethod,
      createdAt: e.created_at ?? e.createdAt,
      receipt: e.receipt ?? null,
    }));
  },

  // Obtener un gasto por ID
  getById: async (id: string): Promise<Expense> => {
    const res = await fetch(`${API_BASE_URL}/expenses/${id}`);
    const data = await handleResponse<any>(res);
    return {
      ...data,
      categoryId: data.category_id ?? data.categoryId,
      paymentMethod: data.payment_method ?? data.paymentMethod,
      createdAt: data.created_at ?? data.createdAt,
      receipt: data.receipt ?? null,
    };
  },

  // Crear un gasto
  create: async (payload: ExpensePayload): Promise<Expense> => {
    const res = await fetch(`${API_BASE_URL}/expenses/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await handleResponse<any>(res);
    return {
      ...data,
      categoryId: data.category_id ?? data.categoryId,
      paymentMethod: data.payment_method ?? data.paymentMethod,
      createdAt: data.created_at ?? data.createdAt,
    };
  },

  // Actualizar un gasto
  update: async (id: string, payload: Partial<ExpensePayload>): Promise<Expense> => {
    const res = await fetch(`${API_BASE_URL}/expenses/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await handleResponse<any>(res);
    return {
      ...data,
      categoryId: data.category_id ?? data.categoryId,
      paymentMethod: data.payment_method ?? data.paymentMethod,
      createdAt: data.created_at ?? data.createdAt,
    };
  },

  // Eliminar un gasto
  remove: async (id: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/expenses/${id}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorBody = await res.text();
      throw new Error(`Error al eliminar: ${res.status} ${errorBody}`);
    }
  },

  // Placeholder para generar IDs (ya no necesario con API real, pero se mantiene)
  buildExpenseId(): string {
    return `exp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  },
};

// ---------- Settings API ----------
export const settingsApi = {
  get: async (): Promise<AppSettings> => {
    const res = await fetch(`${API_BASE_URL}/settings/`);
    return handleResponse<AppSettings>(res);
  },

  update: async (data: Partial<AppSettings>): Promise<AppSettings> => {
    const res = await fetch(`${API_BASE_URL}/settings/`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return handleResponse<AppSettings>(res);
  },
};
// ---------- Cálculos auxiliares ----------
export function computeCategoryTotals(
  expenses: Expense[],
  categories: Category[],
): { category: Category; total: number }[] {
  return categories
    .map((category) => {
      const total = expenses
        .filter((e) => e.categoryId === category.id)
        .reduce((sum, e) => sum + e.amount, 0);
      return { category, total };
    })
    .sort((a, b) => b.total - a.total);
}

export function computeTotals(expenses: Expense[]) {
  const now = new Date();
  const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startWeek = (() => {
    const d = new Date(now);
    const day = d.getDay();
    const diff = day === 0 ? 6 : day - 1;
    d.setDate(d.getDate() - diff);
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  const startPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);

  const thisMonth = expenses.filter((e) => {
    const d = new Date(e.date);
    return d >= startMonth && d <= now;
  });
  const thisWeek = expenses.filter((e) => {
    const d = new Date(e.date);
    return d >= startWeek && d <= now;
  });
  const prevMonth = expenses.filter((e) => {
    const d = new Date(e.date);
    return d >= startPrevMonth && d <= endPrevMonth;
  });

  const monthTotal = thisMonth.reduce((s, e) => s + e.amount, 0);
  const weekTotal = thisWeek.reduce((s, e) => s + e.amount, 0);
  const prevMonthTotal = prevMonth.reduce((s, e) => s + e.amount, 0);

  const daysInMonth = now.getDate();
  const dailyAverage = monthTotal / (daysInMonth || 1);

  return {
    monthTotal,
    weekTotal,
    countThisMonth: thisMonth.length,
    dailyAverage,
    prevMonthTotal,
  };
}
// ---------- Export API (cliente) ----------
export function exportExpenses(expenses: Expense[], settings: AppSettings) {
  const data = JSON.stringify({ expenses, settings }, null, 2);
  const blob = new Blob([data], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "gastos.json";
  a.click();
  URL.revokeObjectURL(url);
}

// ---------- Files API ----------
export interface UploadedFile {
  id?: number;
  drive_file_id: string;
  drive_url: string;
  file_name: string;
  mime_type: string;
  file_size: number;
}

export const filesApi = {
  upload: async (file: File, userId: string = "100"): Promise<UploadedFile> => {
    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("file", file);

    const res = await fetch(`${API_BASE_URL}/files/upload`, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) {
      const errorBody = await res.text();
      throw new Error(`Error al subir archivo: ${res.status} ${errorBody}`);
    }
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.message || "Error al subir archivo");
    }
    return data.data;
  },
};